import React, { useMemo, useState, useEffect } from 'react'
import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { useThree } from '@react-three/fiber'
import { AppObject, MaterialProperties, UvProjection, materialParamsFrom } from '../types'
import { compositePatternCanvas, makeLogoTexture, makePatternTexture } from '../utils/patternTexture'
import { LOGO_ALPHA_TEST } from '../utils/export'
import { MeasurePoint, snapMeasurePoint } from '../utils/measureSnap'

/** primary-500, so a selected part matches the outliner row and the rest of the portal. */
const SELECTION = '#79BC61'

interface ThreeObjectMeshProps {
  object: AppObject
  isSelected: boolean
  onSelect: () => void
  material: MaterialProperties
  uvProjection: UvProjection
  showMeshOnSelect: boolean
  /** When true, dragging across the part measures instead of selecting. */
  measureMode?: boolean
  /** Pointer went down on the part — the measurement starts here (vertex-snapped). */
  onMeasureStart?: (point: MeasurePoint) => void
  /** Pointer moved while measuring — the measurement now ends here (vertex-snapped). */
  onMeasureMove?: (point: MeasurePoint) => void
  /** Pointer over the part while measuring but not dragging — where a press would land. Null on leave. */
  onMeasureHover?: (point: MeasurePoint | null) => void
  /** True between press and release, so a hover after the drag does not move the end. */
  measureDragging?: boolean
  /** When true, clicking the part moves the selected logo to that spot instead of selecting it. */
  pickMode?: boolean
  onPickPoint?: (point: THREE.Vector3) => void
  /** Right-click on the part, for the same menu the outliner rows offer. */
  onContextMenu?: (screenX: number, screenY: number) => void
  /** World-Y center of the part the pattern aligns to (for patternVAlign='object'). */
  alignRefCenterY?: number
}

export const ThreeObjectMesh: React.FC<ThreeObjectMeshProps> = ({
  object,
  isSelected,
  onSelect,
  material,
  uvProjection,
  showMeshOnSelect,
  measureMode,
  onMeasureStart,
  onMeasureMove,
  onMeasureHover,
  measureDragging,
  pickMode,
  onPickPoint,
  onContextMenu,
  alignRefCenterY,
}) => {
  const { mesh } = object
  const { camera, size } = useThree()
  const textureLoader = useMemo(() => new THREE.TextureLoader(), [])

  const geometry = useMemo(() => {
    let geom = new THREE.BufferGeometry()
    if (mesh.attributes?.position?.array) {
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(mesh.attributes.position.array), 3))
    }
    if (mesh.attributes?.normal?.array) {
      geom.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(mesh.attributes.normal.array), 3))
    }
    if (mesh.attributes?.uv?.array) {
      geom.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(mesh.attributes.uv.array), 2))
    }
    if (mesh.attributes?.tangent?.array) {
      geom.setAttribute('tangent', new THREE.BufferAttribute(new Float32Array(mesh.attributes.tangent.array), 4))
    }
    if (mesh.index && mesh.index.array) {
      geom.setIndex(new THREE.BufferAttribute(new Uint32Array(mesh.index.array), 1))
    }

    // Only merge vertices and recompute normals if no normals were provided.
    // GLB files have intentional duplicate vertices at hard edges with different
    // normals — merging them would destroy sharp edge shading.
    if (!geom.attributes.normal) {
      geom = BufferGeometryUtils.mergeVertices(geom, 0.0001)
      geom.computeVertexNormals()
    }
    geom.computeBoundingSphere()
    geom.computeBoundingBox()

    // Fallback: Generate UVs if none exist after merge (for UV test pattern to work)
    if (!geom.attributes.uv && geom.boundingBox) {
      const positions = geom.attributes.position
      const uvs = new Float32Array(positions.count * 2)
      const box = geom.boundingBox
      const size = new THREE.Vector3()
      box.getSize(size)

      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i)
        const y = positions.getY(i)
        uvs[i * 2] = (x - box.min.x) / (size.x || 1)
        uvs[i * 2 + 1] = (y - box.min.y) / (size.y || 1)
      }
      geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
    }

    return geom
  }, [mesh])

  const woodTexture = useMemo(() => {
    if (!object.customTexture) return null
    const tex = textureLoader.load(object.customTexture)
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    return tex
  }, [object.customTexture, textureLoader])

  // The one translation of the panel's values into three.js parameters. Every PBR branch
  // below spreads it, so a control cannot work on one kind of part and be dropped on another.
  const params = useMemo(() => materialParamsFrom(material), [material])

  // Remounts the material whenever any parameter changes (e.g. transmission crossing 0
  // needs a new program). Built from `params` itself so a new parameter is covered too.
  const paramsKey = useMemo(
    () =>
      Object.values(params)
        .map((v) => (v instanceof THREE.Color ? v.getHexString() : String(v)))
        .join('-'),
    [params]
  )

  // Decorative pattern flattened over the base color. Loaded async because the
  // pattern is a data URL. The flattened texture is used as the standard base
  // `map` with a white color, so the preview matches the exported GLB exactly
  // (see utils/patternTexture.ts for why we bake instead of using a live alpha).
  const [patternImg, setPatternImg] = useState<HTMLImageElement | null>(null)
  useEffect(() => {
    if (!material.patternImage) {
      setPatternImg(null)
      return
    }
    let cancelled = false
    const img = new Image()
    img.onload = () => {
      if (!cancelled) setPatternImg(img)
    }
    img.src = material.patternImage
    return () => {
      cancelled = true
    }
  }, [material.patternImage])

  // Logo parts carry their artwork on their own mesh (see utils/logoMesh.ts): the
  // image keeps its alpha and the transparent area is cut away by alphaTest, so the
  // silhouette the user sees is the logo itself, not a rectangle.
  const [logoTexture, setLogoTexture] = useState<THREE.CanvasTexture | null>(null)
  useEffect(() => {
    const src = object.logo?.image
    if (!src) {
      setLogoTexture(null)
      return
    }
    let cancelled = false
    let created: THREE.CanvasTexture | null = null
    const img = new Image()
    img.onload = () => {
      if (cancelled) return
      created = makeLogoTexture(img, object.logo?.knockoutWhite ?? false)
      setLogoTexture(created)
    }
    img.src = src
    return () => {
      cancelled = true
      created?.dispose()
    }
  }, [object.logo?.image, object.logo?.knockoutWhite])

  // Circumference:height of this part — lets the pattern keep its aspect ratio when not filling.
  const objectAspect = useMemo(() => {
    const bb = geometry.boundingBox
    if (!bb) return undefined
    const radius = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) / 2
    const height = bb.max.y - bb.min.y
    return height > 0 ? (2 * Math.PI * radius) / height : undefined
  }, [geometry])

  // Vertical anchor for 'object' align: where the reference part's center lands in
  // this part's UV. Falls back to logo_main's precomputed position.
  const alignV = useMemo(() => {
    const bb = geometry.boundingBox
    if (alignRefCenterY === undefined || !bb) return object.logoV
    const h = bb.max.y - bb.min.y
    return h > 0 ? 1 - (alignRefCenterY - bb.min.y) / h : object.logoV
  }, [geometry, alignRefCenterY, object.logoV])

  const patternTexture = useMemo(() => {
    if (!material.patternImage || !patternImg) return null
    const canvas = compositePatternCanvas(material.color, patternImg, {
      repeatX: material.patternRepeatX ?? 1,
      repeatY: material.patternRepeatY ?? 1,
      offsetX: material.patternOffsetX ?? 0,
      offsetY: material.patternOffsetY ?? 0,
      scale: material.patternScale ?? 1,
      fill: material.patternFill ?? false,
      objectAspect,
      vAlign: material.patternVAlign ?? 'center',
      logoV: alignV,
    })
    return makePatternTexture(canvas)
  }, [
    material.patternImage,
    patternImg,
    material.color,
    material.patternRepeatX,
    material.patternRepeatY,
    material.patternOffsetX,
    material.patternOffsetY,
    material.patternScale,
    material.patternFill,
    material.patternVAlign,
    objectAspect,
    alignV,
  ])

  // Free the GPU texture when it is replaced or the mesh unmounts.
  useEffect(() => {
    return () => {
      patternTexture?.dispose()
    }
  }, [patternTexture])

  /**
   * A part whose file authored its own PBR maps renders with them, the panel's values
   * riding on top as glTF defines them: baseColorFactor, metallicFactor and roughnessFactor
   * MULTIPLY the maps. The imported white/1.0/1.0 is therefore pass-through — the model
   * looks as its author intended until someone actually moves a slider, and then the whole
   * surface tints or dulls together instead of the map being replaced by a flat number.
   *
   * Every other parameter, Specular and Environment included, comes from the panel as on
   * any other part — the maps are the only thing this branch adds.
   */
  const authoredMaterial = useMemo(() => {
    const src = object.sourceMaterial
    if (!src) return null
    const m = new THREE.MeshPhysicalMaterial({
      ...params,
      map: src.map,
      normalMap: src.normalMap,
      aoMap: src.aoMap,
      metalnessMap: src.metalnessMap,
      roughnessMap: src.roughnessMap,
      emissiveMap: src.emissiveMap,
      alphaMap: src.alphaMap,
    })
    if (src.normalScale) m.normalScale.copy(src.normalScale)
    m.aoMapIntensity = src.aoMapIntensity ?? 1
    return m
  }, [object.sourceMaterial, params])

  // The maps belong to the loaded GLTF, which owns their lifetime; only the wrapper is ours.
  useEffect(() => {
    return () => {
      authoredMaterial?.dispose()
    }
  }, [authoredMaterial])

  // Helper function to create UV test canvas
  const createUvTestCanvas = (showPattern: boolean, showText: boolean, textLabel: string): HTMLCanvasElement | null => {
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 512
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    if (showPattern) {
      // Subtle blue checkerboard pattern
      const squareSize = 64
      for (let y = 0; y < canvas.height; y += squareSize) {
        for (let x = 0; x < canvas.width; x += squareSize) {
          const isEven = (x / squareSize + y / squareSize) % 2 === 0
          ctx.fillStyle = isEven ? '#4a90d9' : '#2d5a8a'
          ctx.fillRect(x, y, squareSize, squareSize)
        }
      }
      // Draw border
      ctx.strokeStyle = '#ff6600'
      ctx.lineWidth = 8
      ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8)
    } else {
      // Transparent background when only text is shown
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }

    if (showText) {
      // Draw text with outline for visibility
      ctx.fillStyle = '#ffffff'
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 3
      ctx.font = 'bold 48px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.strokeText('Wrapping', canvas.width / 2, canvas.height / 2 - 30)
      ctx.fillText('Wrapping', canvas.width / 2, canvas.height / 2 - 30)
      ctx.strokeText(textLabel, canvas.width / 2, canvas.height / 2 + 30)
      ctx.fillText(textLabel, canvas.width / 2, canvas.height / 2 + 30)

      // Draw UV coordinates markers
      ctx.font = 'bold 24px Arial'
      ctx.fillStyle = '#ffff00'
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 2
      ctx.strokeText('(0,0)', 50, 30)
      ctx.fillText('(0,0)', 50, 30)
      ctx.strokeText('(1,0)', canvas.width - 50, 30)
      ctx.fillText('(1,0)', canvas.width - 50, 30)
      ctx.strokeText('(0,1)', 50, canvas.height - 20)
      ctx.fillText('(0,1)', 50, canvas.height - 20)
      ctx.strokeText('(1,1)', canvas.width - 50, canvas.height - 20)
      ctx.fillText('(1,1)', canvas.width - 50, canvas.height - 20)
    }

    return canvas
  }

  // Create UV test textures (front and back)
  const uvTestTextures = useMemo(() => {
    if (!material.showUvTestText && !material.showUvTestPattern) return null

    const frontCanvas = createUvTestCanvas(material.showUvTestPattern, material.showUvTestText, 'Front')
    const backCanvas = createUvTestCanvas(material.showUvTestPattern, material.showUvTestText, 'Back')

    if (!frontCanvas || !backCanvas) return null

    const frontTexture = new THREE.CanvasTexture(frontCanvas)
    frontTexture.wrapS = THREE.RepeatWrapping
    frontTexture.wrapT = THREE.RepeatWrapping
    frontTexture.flipY = false

    const backTexture = new THREE.CanvasTexture(backCanvas)
    backTexture.wrapS = THREE.RepeatWrapping
    backTexture.wrapT = THREE.RepeatWrapping
    backTexture.flipY = false

    return { front: frontTexture, back: backTexture }
  }, [material.showUvTestText, material.showUvTestPattern])

  // UV test shader definition - supports separate front/back textures
  const uvTestShader = useMemo(
    () => ({
      vertexShader: `
      varying vec3 vLocalPosition;
      varying vec3 vLocalNormal;
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      void main() {
        vLocalPosition = position;
        vLocalNormal = normal;
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPos.xyz;
        vWorldNormal = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
      fragmentShader: `
      uniform sampler2D uTextureFront;
      uniform sampler2D uTextureBack;
      uniform vec3 uBboxMin;
      uniform vec3 uBboxMax;
      uniform int uProjectionType;
      uniform vec3 uCameraPosition;
      varying vec3 vLocalPosition;
      varying vec3 vLocalNormal;
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;

      void main() {
        // Calculate view direction and check if surface faces camera
        vec3 viewDir = normalize(uCameraPosition - vWorldPosition);
        float facingCamera = dot(vWorldNormal, viewDir);

        // Only render text on surfaces facing the camera
        // This prevents seeing text through transparent materials
        if (facingCamera < 0.0) {
          discard;
        }

        vec3 size = uBboxMax - uBboxMin;
        vec2 uv;

        if (uProjectionType == 0) {
          // Cylindrical UV mapping
          float theta = atan(vLocalPosition.x, vLocalPosition.z);
          uv.x = (theta + 3.14159265) / (2.0 * 3.14159265);
          uv.y = 1.0 - (vLocalPosition.y - uBboxMin.y) / size.y;
        } else if (uProjectionType == 1) {
          // Box UV mapping - project based on dominant normal direction
          vec3 absNormal = abs(vLocalNormal);
          if (absNormal.x >= absNormal.y && absNormal.x >= absNormal.z) {
            // X-facing - use Z and Y
            uv.x = (vLocalPosition.z - uBboxMin.z) / size.z;
            uv.y = 1.0 - (vLocalPosition.y - uBboxMin.y) / size.y;
          } else if (absNormal.y >= absNormal.x && absNormal.y >= absNormal.z) {
            // Y-facing - use X and Z
            uv.x = (vLocalPosition.x - uBboxMin.x) / size.x;
            uv.y = 1.0 - (vLocalPosition.z - uBboxMin.z) / size.z;
          } else {
            // Z-facing - use X and Y
            uv.x = (vLocalPosition.x - uBboxMin.x) / size.x;
            uv.y = 1.0 - (vLocalPosition.y - uBboxMin.y) / size.y;
          }
        } else {
          // Planar UV mapping - project from front (Z direction)
          uv.x = (vLocalPosition.x - uBboxMin.x) / size.x;
          uv.y = 1.0 - (vLocalPosition.y - uBboxMin.y) / size.y;
        }

        // Determine front vs back based on world normal Z direction
        // Front surfaces face toward +Z (toward initial camera position)
        // Back surfaces face toward -Z
        bool isFrontSurface = vWorldNormal.z > 0.0;

        vec4 texColor;
        if (isFrontSurface) {
          texColor = texture2D(uTextureFront, uv);
        } else {
          // Flip U coordinate for back surface to prevent mirroring
          texColor = texture2D(uTextureBack, vec2(1.0 - uv.x, uv.y));
        }
        gl_FragColor = texColor;
      }
    `,
    }),
    []
  )

  // Inverted hull outline shader (Blender-style thick silhouette outline)
  const outlineShader = useMemo(
    () => ({
      vertexShader: `
      uniform float uThickness;
      void main() {
        // Push vertices outward along their normals
        vec3 pos = position + normal * uThickness;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `,
      fragmentShader: `
      uniform vec3 uColor;
      void main() {
        gl_FragColor = vec4(uColor, 1.0);
      }
    `,
    }),
    []
  )

  // Calculate outline thickness based on geometry size
  const outlineThickness = useMemo(() => {
    if (!geometry.boundingBox) return 0.5
    const size = new THREE.Vector3()
    geometry.boundingBox.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z)
    return maxDim * 0.005 // 0.5% of largest dimension
  }, [geometry])

  const showTextOnly = material.showUvTestText && !material.showUvTestPattern
  const showPatternOrBoth = material.showUvTestPattern

  return (
    <group position={object.offset ?? [0, 0, 0]} visible={!object.hidden}>
      {/* Base mesh with material */}
      <mesh
        geometry={geometry}
        onPointerDown={(e) => {
          if (!measureMode) return
          e.stopPropagation()
          // Alt places a free point, for the rare spot between vertices.
          onMeasureStart?.(snapMeasurePoint(e, e.camera, size, !e.nativeEvent.altKey))
        }}
        onPointerMove={(e) => {
          if (!measureMode) return
          e.stopPropagation()
          const hit = snapMeasurePoint(e, e.camera, size, !e.nativeEvent.altKey)
          if (measureDragging) onMeasureMove?.(hit)
          else onMeasureHover?.(hit)
        }}
        onPointerOut={() => {
          if (measureMode && !measureDragging) onMeasureHover?.(null)
        }}
        onContextMenu={(e) => {
          if (measureMode || pickMode) return
          e.stopPropagation()
          e.nativeEvent.preventDefault()
          e.nativeEvent.stopPropagation()
          onContextMenu?.(e.nativeEvent.clientX, e.nativeEvent.clientY)
        }}
        onClick={(e) => {
          e.stopPropagation()
          // Measuring is handled by the drag above; a click must not also select.
          if (measureMode) return
          if (pickMode) {
            onPickPoint?.(e.point.clone())
            return
          }
          onSelect()
        }}
      >
        {object.logo ? (
          // Logo sheet: the artwork's shape (a white mask, see makeLogoTexture), cut out
          // by alphaTest, in the Base colour. A logo follows the shared material like any
          // other part until given its own; its colour is the panel's, not the file's.
          //
          // The surface controls all apply, exactly as on any other part.
          //
          // Deliberate exception, structural rather than a look: the sheet is always
          // `transparent` with alphaTest, because the artwork's alpha IS the cut-out
          // silhouette; and polygonOffset biases it toward the camera in depth space so it
          // wins over the part beneath it whatever the depth-buffer precision, instead of
          // relying on the physical standoff alone.
          //
          // The artwork decodes asynchronously, and three only compiles a material's shader
          // once: a `map` set on a material that has already been drawn is never sampled
          // (R3F does not flag needsUpdate). Drawn before its texture arrived, the sheet
          // stayed a plain rectangle for good. So nothing is drawn until the texture exists,
          // and the texture's identity is in the key, so a new one (e.g. toggling the white
          // knockout) builds a fresh material instead of being attached to a compiled one.
          logoTexture ? (
            <meshPhysicalMaterial
              key={`logo-${logoTexture.uuid}-${paramsKey}`}
              {...params}
              map={logoTexture}
              transparent
              alphaTest={LOGO_ALPHA_TEST}
              polygonOffset
              polygonOffsetFactor={-4}
              polygonOffsetUnits={-4}
            />
          ) : (
            <meshBasicMaterial key="logo-loading" visible={false} />
          )
        ) : showPatternOrBoth && uvTestTextures ? (
          // Show checkerboard pattern (optionally with text)
          <shaderMaterial
            key={`uv-test-shader-${uvProjection}-pattern`}
            uniforms={{
              uTextureFront: { value: uvTestTextures.front },
              uTextureBack: { value: uvTestTextures.back },
              uBboxMin: { value: geometry.boundingBox?.min || new THREE.Vector3() },
              uBboxMax: { value: geometry.boundingBox?.max || new THREE.Vector3() },
              uProjectionType: { value: uvProjection === 'cylindrical' ? 0 : uvProjection === 'box' ? 1 : 2 },
              uCameraPosition: { value: camera.position },
            }}
            vertexShader={uvTestShader.vertexShader}
            fragmentShader={uvTestShader.fragmentShader}
            side={params.side}
          />
        ) : woodTexture ? (
          <shaderMaterial
            uniforms={{ uTexture: { value: woodTexture }, uScale: { value: 0.02 } }}
            vertexShader={`
              varying vec3 vNormal;
              varying vec3 vWorldPosition;
              void main() {
                vNormal = normalize(normalMatrix * normal);
                vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                vWorldPosition = worldPosition.xyz;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }
            `}
            fragmentShader={`
              uniform sampler2D uTexture;
              uniform float uScale;
              varying vec3 vNormal;
              varying vec3 vWorldPosition;
              void main() {
                vec3 blending = abs(vNormal);
                blending = pow(blending, vec3(8.0));
                blending /= (blending.x + blending.y + blending.z);

                vec3 xTex = texture2D(uTexture, vWorldPosition.yz * uScale).rgb;
                vec3 yTex = texture2D(uTexture, vWorldPosition.xz * uScale).rgb;
                vec3 zTex = texture2D(uTexture, vWorldPosition.xy * uScale).rgb;
                gl_FragColor = vec4(xTex * blending.x + yTex * blending.y + zTex * blending.z, 1.0);
              }
            `}
          />
        ) : authoredMaterial ? (
          // The file brought its own surface. Everything that makes this part
          // recognisable — lettering, wear, stipple, which faces read as steel and which
          // as polymer — lives in its normal/AO/metal-rough maps, not in its geometry, so
          // it is drawn with them rather than with a colour and two sliders.
          <primitive object={authoredMaterial} attach="material" />
        ) : patternTexture ? (
          // Pattern printed over the base color. Every surface control applies.
          //
          // Deliberate exception: color is white, because the base colour is already
          // baked into the pattern texture (compositePatternCanvas) and multiplying by it
          // again would tint twice. The export does the same (createExportMaterial).
          <meshPhysicalMaterial key={`pattern-${paramsKey}`} {...params} map={patternTexture} color={0xffffff} />
        ) : (
          <meshPhysicalMaterial key={paramsKey} {...params} />
        )}
      </mesh>

      {/* Text overlay (only when text is enabled without pattern) */}
      {showTextOnly && uvTestTextures && (
        <mesh geometry={geometry}>
          <shaderMaterial
            key={`uv-test-shader-${uvProjection}-text-overlay`}
            uniforms={{
              uTextureFront: { value: uvTestTextures.front },
              uTextureBack: { value: uvTestTextures.back },
              uBboxMin: { value: geometry.boundingBox?.min || new THREE.Vector3() },
              uBboxMax: { value: geometry.boundingBox?.max || new THREE.Vector3() },
              uProjectionType: { value: uvProjection === 'cylindrical' ? 0 : uvProjection === 'box' ? 1 : 2 },
              uCameraPosition: { value: camera.position },
            }}
            vertexShader={uvTestShader.vertexShader}
            fragmentShader={uvTestShader.fragmentShader}
            side={params.side}
            transparent
            depthTest={false}
          />
        </mesh>
      )}

      {/* Orange outline when selected (inverted hull / Blender-style) */}
      {isSelected && (
        <mesh geometry={geometry}>
          <shaderMaterial
            vertexShader={outlineShader.vertexShader}
            fragmentShader={outlineShader.fragmentShader}
            uniforms={{
              uThickness: { value: outlineThickness },
              uColor: { value: new THREE.Color(SELECTION) },
            }}
            side={THREE.BackSide}
          />
        </mesh>
      )}

      {/*
        Selection is shown by the outline above, which sits OUTSIDE the silhouette and so
        never touches the surface. It used to be paired with a flat tint painted over the
        part, but that tint is mixed into everything underneath it: with a selected part
        washed 30% toward the highlight colour, re-colouring it barely moves what you see,
        which makes the material controls look broken at the exact moment they are in use.

        A flat logo sheet is the one case that needs the tint, because an inverted-hull
        outline only works on a closed volume and is invisible on a single surface. There
        the artwork's own alpha masks the tint, so it marks the letters rather than
        painting a rectangle.
      */}
      {isSelected && object.logo && !object.logo.thickness && logoTexture && (
        <mesh geometry={geometry} renderOrder={999}>
          {/* Keyed on the texture for the same reason as the sheet: a late map is never sampled. */}
          <meshBasicMaterial
            key={logoTexture.uuid}
            color={SELECTION}
            map={logoTexture}
            alphaTest={LOGO_ALPHA_TEST}
            transparent
            opacity={0.35}
            depthWrite={false}
            polygonOffset
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {/* Full wireframe mesh (optional, toggled by checkbox) */}
      {isSelected && showMeshOnSelect && (
        <mesh geometry={geometry}>
          <meshStandardMaterial
            wireframe
            color={SELECTION}
            emissive={SELECTION}
            emissiveIntensity={2}
            depthTest={false}
            transparent
            opacity={0.8}
          />
        </mesh>
      )}
    </group>
  )
}
